// WinAnsiEncoding codes for typographic characters outside Latin-1, so
// template wording (curly quotes, dashes) prints instead of becoming "?".
const WIN_ANSI_EXTRAS = {
  '\u2018': 0x91,
  '\u2019': 0x92,
  '\u201C': 0x93,
  '\u201D': 0x94,
  '\u2022': 0x95,
  '\u2013': 0x96,
  '\u2014': 0x97,
  '\u2026': 0x85,
  '\u2122': 0x99,
};

function winAnsiCode(char) {
  if (WIN_ANSI_EXTRAS[char] !== undefined) return WIN_ANSI_EXTRAS[char];
  const code = char.codePointAt(0);
  return code >= 0xA0 && code <= 0xFF ? code : null;
}

export function pdfEscape(text) {
  return Array.from(String(text)).map((char) => {
    if (char === '\\' || char === '(' || char === ')') return `\\${char}`;
    if (char === '\t' || (char >= ' ' && char <= '~')) return char;
    const code = winAnsiCode(char);
    return code === null ? '?' : `\\${code.toString(8).padStart(3, '0')}`;
  }).join('');
}

export function wrapLine(line, max = 92) {
  const source = line === '' || line === undefined || line === null ? ' ' : String(line);
  if (source.length <= max) return [source];

  const words = source.split(/\s+/);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > max && current) {
      lines.push(current);
      current = word.length > max ? word.slice(0, max) : word;
      while (word.length > max) {
        word = word.slice(max);
        if (word) {
          lines.push(current);
          current = word.slice(0, max);
        }
      }
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [' '];
}

function pageStream(lines, { title, pageNumber, pageCount }) {
  const commands = [
    'BT',
    '/F1 11 Tf',
    '14 TL',
    '72 744 Td',
    `(${pdfEscape(title || 'VASI Cloud Eval Report')}) Tj`,
    'T*',
    '/F1 9 Tf',
    `(Page ${pageNumber} of ${pageCount}) Tj`,
    'T*',
    'T*',
    '/F1 10 Tf',
  ];

  lines.forEach((line) => {
    commands.push(`(${pdfEscape(line)}) Tj`);
    commands.push('T*');
  });
  commands.push('ET');
  return commands.join('\n');
}

function xrefTable(offsets) {
  const lines = ['xref', `0 ${offsets.length}`];
  offsets.forEach((offset, index) => {
    if (index === 0) {
      lines.push('0000000000 65535 f ');
    } else {
      lines.push(`${String(offset).padStart(10, '0')} 00000 n `);
    }
  });
  return lines.join('\n');
}

/**
 * Minimal text-only PDF. Clinical wording is passed in — never bundled here.
 */
export function buildTextPdf({ title, lines } = {}) {
  const wrapped = [];
  (lines ?? []).forEach((line) => {
    wrapLine(line).forEach((part) => wrapped.push(part));
  });
  if (wrapped.length === 0) wrapped.push(' ');

  const linesPerPage = 46;
  const pages = [];
  for (let index = 0; index < wrapped.length; index += linesPerPage) {
    pages.push(wrapped.slice(index, index + linesPerPage));
  }

  const objects = [];
  objects[1] = null; // catalog, filled after pages object exists
  objects[2] = null; // pages
  const fontObject = 3 + pages.length * 2;
  objects[fontObject] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';

  const pageObjectIds = pages.map((_, index) => 3 + index * 2);
  pages.forEach((pageLines, index) => {
    const pageObject = pageObjectIds[index];
    const contentObject = pageObject + 1;
    const stream = pageStream(pageLines, {
      title,
      pageNumber: index + 1,
      pageCount: pages.length,
    });
    objects[pageObject] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents ${contentObject} 0 R /Resources << /Font << /F1 ${fontObject} 0 R >> >> >>`;
    objects[contentObject] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  });

  objects[1] = `<< /Type /Catalog /Pages 2 0 R >>`;
  objects[2] = `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`;

  let body = '%PDF-1.4\n';
  const offsets = [0];
  for (let id = 1; id <= fontObject; id += 1) {
    offsets[id] = body.length;
    body += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }

  const xrefOffset = body.length;
  body += `${xrefTable(offsets)}\n`;
  body += `trailer\n<< /Size ${fontObject + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  return body;
}

export function reportHeading(assembled) {
  return assembled?.area === 'legacy' ? 'VASI Legacy Eval Report' : 'VASI Cloud Eval Report';
}

export function reportScoreNote(assembled) {
  return assembled?.area === 'legacy'
    ? 'Scores (DA from legacy chart bars: Image vs ctrl-Image ± error; Raw uses AVG ± AVG×STD_DEV; HS/CS/D are stubs unless overridden)'
    : 'Scores (DA from Normalized Image / avg / sd; HS/CS/D are stubs unless overridden)';
}

export function reportLines(assembled) {
  const lines = [
    reportHeading(assembled),
    assembled.clientName ? `Client: ${assembled.clientName}` : 'Client: (unnamed)',
    assembled.caseNumber ? `Case: ${assembled.caseNumber}` : 'Case: (none)',
    `Sex: ${assembled.sex || 'unknown'}`,
    assembled.templateVersion != null
      ? `Pinned template version: ${assembled.templateVersion}`
      : 'Pinned template version: none (codes-only)',
    '',
    reportScoreNote(assembled),
  ];

  const codes = assembled.codes;
  if (codes?.caution) {
    lines.push(`CS: ${codes.caution.cs ?? 'unscored'}${codes.caution.stub ? ' (stub)' : ''}`);
  }

  Object.entries(codes?.groups ?? {}).forEach(([groupId, scored]) => {
    const dPart = scored.d !== null && scored.d !== undefined ? ` D=${scored.d}` : '';
    lines.push(
      `${groupId}: SA=${scored.sa ?? '—'} DA=${scored.da ?? '—'} HS=${scored.hs ?? '—'}${dPart}`
    );
  });

  lines.push('');
  if (assembled.codesOnly) {
    lines.push('No published prose for this sex/version. Codes-only report.');
  } else {
    lines.push('Interpretation');
    assembled.sections.forEach((section) => {
      lines.push('');
      lines.push(`[${section.key}]`);
      lines.push(section.missing ? '(No published prose for this key.)' : section.text);
    });
  }

  return lines;
}

export function buildReportPdfBlob(assembled) {
  const pdf = buildTextPdf({
    title: reportHeading(assembled),
    lines: reportLines(assembled),
  });
  return new Blob([pdf], { type: 'application/pdf' });
}

export function downloadBlob(blob, filename, { documentRef = document, urlApi = URL } = {}) {
  const url = urlApi.createObjectURL(blob);
  const anchor = documentRef.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  documentRef.body?.appendChild?.(anchor);
  anchor.click();
  anchor.remove?.();
  urlApi.revokeObjectURL(url);
}
